import _ from "lodash";
// Used to get media-wise options (i.e download, expand etc.) on right side of theatre mdoe v2
import React from "react";
import {
  DownloadOutlined,
  CollapseArrowsTwoOutlined,
  ExpandArrowsFourOutlined,
  RefreshArrowOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ArrowOutOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";

export const getMediaOptions = configurations => {
  const COMMON_OPTIONS = [
    {
      key: "common_download",
      icon: <DownloadOutlined variant="default" />,
      onClick: e => {
        if (_.isFunction(_.get(configurations, "onDownloadClick")))
          configurations?.onDownloadClick(
            _.get(configurations, "attachment", {})
          );
      },
      permissions: ["CAN_DOWNLOAD"],
      label: "common:download",
    },
  ];
  if (_.get(configurations, "showExpandOption", false)) {
    COMMON_OPTIONS.push({
      key: "common_expandView",
      icon: _.get(configurations, "isExpandedView", false) ? (
        <CollapseArrowsTwoOutlined variant="default" />
      ) : (
        <ExpandArrowsFourOutlined variant="default" />
      ),
      onClick: e => {
        if (_.isFunction(_.get(configurations, "onExpandViewClick")))
          configurations?.onExpandViewClick(e);
      },
      hasDivider: true,
      label: _.get(configurations, "isExpandedView", false)
        ? "common:collapse"
        : "common:expand",
    });
  }
  const getCommonOption = key =>
    _.find(COMMON_OPTIONS, option => option.key == key);
  const attachment = _.get(configurations, "attachment", {});
  const shouldShowEditResponse = _.get(
    attachment,
    "shouldShowEditResponse",
    true
  );

  const MEDIA_OPTIONS = {
    IMAGE: [
      { ...getCommonOption("common_expandView") },
      {
        key: "image_rotation",
        icon: <RefreshArrowOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onRotateImageClick")))
            configurations?.onRotateImageClick(e);
        },
        label: "common:rotate",
      },
      {
        key: "image_zoomout",
        icon: <ZoomOutOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onImageZoomOutClick")))
            configurations?.onImageZoomOutClick(e);
        },
        label: "common:zoom_out",
      },
      {
        key: "image_zoomin",
        icon: <ZoomInOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onImageZoomInClick")))
            configurations?.onImageZoomInClick(e);
        },
        hasDivider: true,
        label: "common:zoom_in",
      },
      { ...getCommonOption("common_download") },
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["EDIT_MODE_ONLY"],
        variant: "outlined",
        label: shouldShowEditResponse
          ? "common:edit_with_label"
          : "common:add_with_label",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick")))
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    VIDEO: [
      { ...getCommonOption("common_expandView") },
      { ...getCommonOption("common_download") },
    ],
    AUDIO: [
      { ...getCommonOption("common_expandView") },
      { ...getCommonOption("common_download") },
    ],
    LINK: [
      { ...getCommonOption("common_expandView") },
      { ...getCommonOption("common_download") },
      {
        key: "open_link_in_new_tab",
        buttonType: "plainButton",
        permissions: ["CAN_OPEN_IN_NEW_TAB"],
        icon: <ArrowOutOutlined variant="default" />,
        variant: "outlined",
        label: "common:open_in_label",
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onOpenLinkInNewTabClick")))
            configurations?.onOpenLinkInNewTabClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    PDF: [
      { ...getCommonOption("common_expandView") },
      { ...getCommonOption("common_download") },
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["EDIT_MODE_ONLY"],
        variant: "outlined",
        label: "common:edit_with_label",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick"))) {
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
          }
        },
      },
    ],
    NOTE: [
      { ...getCommonOption("common_expandView") },
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["NOTES_EDIT_MODE"],
        variant: "outlined",
        label: "common:edit",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick")))
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    WORKBOOK: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
        permissions: ["HAS_DOWNLOAD_URL", "CAN_DOWNLOAD"],
      },
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["EDIT_MODE_ONLY"],
        variant: "outlined",
        label: shouldShowEditResponse
          ? "common:edit_with_label"
          : "common:add_with_label",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick")))
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    CODE_EDITOR: [
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["EDIT_MODE_ONLY"],
        variant: "outlined",
        label: shouldShowEditResponse
          ? "common:edit_with_label"
          : "common:add_with_label",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick")))
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    TODDLE_DOC: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
      },
      {
        key: "edit_attachment",
        buttonType: "plainButton",
        permissions: ["EDIT_MODE_ONLY"],
        variant: "outlined",
        label: shouldShowEditResponse
          ? "common:edit_with_label"
          : "common:add_with_label",
        icon: <PencilLineOutlined variant="default" />,
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onEditButtonClick")))
            configurations?.onEditButtonClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    DOCUMENT: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
        permissions: ["CAN_DOWNLOAD", "SHOULD_DOWNLOAD_BY_URL"],
      },
      {
        key: "open_link_in_new_tab",
        buttonType: "plainButton",
        permissions: ["CAN_OPEN_IN_NEW_TAB"],
        icon: <ArrowOutOutlined variant="default" />,
        variant: "outlined",
        label: "common:open_in_label",
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onOpenLinkInNewTabClick")))
            configurations?.onOpenLinkInNewTabClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    SHEET: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
        permissions: ["CAN_DOWNLOAD", "SHOULD_DOWNLOAD_BY_URL"],
      },
      {
        key: "open_link_in_new_tab",
        buttonType: "plainButton",
        permissions: ["CAN_OPEN_IN_NEW_TAB"],
        icon: <ArrowOutOutlined variant="default" />,
        variant: "outlined",
        label: "common:open_in_label",
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onOpenLinkInNewTabClick")))
            configurations?.onOpenLinkInNewTabClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    SLIDES: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
        permissions: ["CAN_DOWNLOAD", "SHOULD_DOWNLOAD_BY_URL"],
      },
      {
        key: "open_link_in_new_tab",
        buttonType: "plainButton",
        permissions: ["CAN_OPEN_IN_NEW_TAB"],
        icon: <ArrowOutOutlined variant="default" />,
        variant: "outlined",
        label: "common:open_in_label",
        onClick: e => {
          if (_.isFunction(_.get(configurations, "onOpenLinkInNewTabClick")))
            configurations?.onOpenLinkInNewTabClick(
              _.get(configurations, "attachment", {})
            );
        },
      },
    ],
    ZIP: [
      { ...getCommonOption("common_expandView") },
      { ...getCommonOption("common_download") },
    ],
    ASSESSMENT_TOOL: [
      { ...getCommonOption("common_expandView") },
      {
        ...getCommonOption("common_download"),
        permissions: ["CAN_DOWNLOAD", "CAN_DOWNLOAD_ASSESSMENT_TOOL"],
      },
    ],
    POST_RATE_LEARNING_GOALS: [{ ...getCommonOption("common_expandView") }],
  };
  return MEDIA_OPTIONS;
};
